import React, { useEffect, useRef, Fragment, useState, forwardRef } from "react"
import * as dateHelper from "../helpers/date"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "../../helpers/strings"
import { parkingState } from "../zustand/parkingState"
import { performChangeBookingSearch, performParkingSearch, performChangeBookingExitTime, performChangeBookingTime } from "../helpers/apiHelper"
import * as path from "../constants/Paths"
import { FlowEnum } from "../constants/flowEnums"
import { getQueryStringValue, setQueryStringValue } from "../../api/queryStringHelper"
import { DateTime } from "luxon"
import da from "date-fns/locale/da"
import enGB from "date-fns/locale/en-GB"

const CPH = "Europe/Copenhagen"

// Utility function to parse date strings with multiple formats
const parseDateWithFormats = (dateString, formats, options = {}) => {
  for (const format of formats) {
    const parsedDate = DateTime.fromFormat(dateString, format, { locale: options.locale, zone: CPH }).setZone("local", { keepLocalTime: true })
    if (parsedDate.isValid) {
      return parsedDate
    }
  }
  return null // Return null if no format matches
}

// Supported date formats
const supportedDateFormats = ["yyyy-MM-dd", "dd / MM / yyyy", "yyyy / MM / dd", "dd / MM / yyyy HH:mm", "yyyy / MM / dd h:mm aa", "yyyy-MM-dd HH:mm"]

// filter to remove DST hour
const filterDstHour = (time) => {
  const t = new Date(time)

  if (t.getMonth() === 2 && t.getDay() === 0 && t.getDate() >= 25 && t.getHours() === 2) {
    return false
  }
  return true
}

const getLocalizedChooseText = (date, locale) => {
  const localizedChooseText = locale === "da" ? "Vælg" : "Choose" // Example for Danish and English
  return `${localizedChooseText} ${date.toLocaleDateString(locale, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}`
}

export const ParkingDatePicker = ({
  location,
  flow,
  slimVersion,
  textKeys,
  button,
  buttonClasses,
  buttonFontColor,
  buttonModifiers,
  leadTimeInHours,
  searchData,
  isInModal,
  history,
}) => {
  const containerRef = useRef(null)
  const mobileCloseBtnRef = useRef(null)
  const fromDatePickerRef = useRef(null)
  const fromTimePickerRef = useRef(null)
  const toDatePickerRef = useRef(null)
  const toTimePickerRef = useRef(null)
  const fromDateSelectRef = useRef(null)
  const toDateSelectRef = useRef(null)
  const fromTimeWrapperRef = useRef(null)
  const toTimeWrapperRef = useRef(null)

  const [showMobileBackdrop, setShowMobileBackdrop] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 950)
  const [focusedCampaignCodeField, setFocusedCampaignCodeField] = useState(false)

  const handleOpenMobileBackdrop = () => {
    setShowMobileBackdrop((prev) => !prev)
  }

  useEffect(() => {
    if (showMobileBackdrop && currentFlow === FlowEnum.AMENDMENTFLOWCHANGECARPARK) {
      if (fromDatePickerRef && fromDatePickerRef.current) {
        fromDatePickerRef.current.setFocus()
      }
      return
    }

    if (showMobileBackdrop) {
      mobileCloseBtnRef.current?.focus()
      return
    }
  }, [showMobileBackdrop])

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      if (fromDatePickerRef.current) fromDatePickerRef.current.setOpen(false)
      if (fromTimePickerRef.current) fromTimePickerRef.current.setOpen(false)
      if (toDatePickerRef.current) toDatePickerRef.current.setOpen(false)
      if (toTimePickerRef.current) toTimePickerRef.current.setOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  useEffect(() => {
    const handleFocusIn = (event) => {
      if (fromDateSelectRef.current?.contains(event.target)) {
        if (fromTimeWrapperRef.current?.contains(event.target)) {
          if (fromDatePickerRef.current) fromDatePickerRef.current.setOpen(false)
        }
      } else if (!fromDateSelectRef.current?.contains(event.target)) {
        if (fromDatePickerRef.current) fromDatePickerRef.current.setOpen(false)
      }

      if (fromTimeWrapperRef.current?.contains(event.target)) {
        if (toDateSelectRef.current?.contains(event.target)) {
          if (fromTimePickerRef.current) fromTimePickerRef.current.setOpen(false)
        }
      } else if (!fromTimeWrapperRef.current?.contains(event.target)) {
        if (fromTimePickerRef.current) fromTimePickerRef.current.setOpen(false)
      }

      if (toDateSelectRef.current?.contains(event.target)) {
        if (toTimeWrapperRef.current?.contains(event.target)) {
          if (toDatePickerRef.current) toDatePickerRef.current.setOpen(false)
        }
      } else if (!toDateSelectRef.current?.contains(event.target)) {
        if (toDatePickerRef.current) toDatePickerRef.current.setOpen(false)
      }

      if (toTimeWrapperRef.current?.contains(event.target)) {
        if (!toDateSelectRef.current?.contains(event.target)) {
          if (toTimePickerRef.current) toTimePickerRef.current.setOpen(false)
        }
      } else if (!toTimeWrapperRef.current?.contains(event.target)) {
        if (toTimePickerRef.current) toTimePickerRef.current.setOpen(false)
      }
    }

    document.addEventListener("focusin", handleFocusIn)

    return () => {
      document.removeEventListener("focusin", handleFocusIn)
    }
  }, [])

  let queryAd = getQueryStringValue("ad")
  let queryAt = getQueryStringValue("at")
  let queryDd = getQueryStringValue("dd")
  let queryDt = getQueryStringValue("dt")

  const locale = document.querySelector("html").getAttribute("lang")
  const dtLocale = dateHelper.luxonLocale(locale)

  const currentTime = DateTime.local().setLocale(dtLocale).setZone(CPH)
  const cphTime = currentTime.setZone("local", { keepLocalTime: true })

  const arrivalLeadTimeHours = leadTimeInHours || textKeys.leadTimeInHours || 1.0
  const arrivalLeadTimeMinutes = arrivalLeadTimeHours * 60

  const cphLeadTime = cphTime.plus({ minutes: arrivalLeadTimeMinutes })
  const initialAbsoluteMinDateTime = dateHelper.RoundToNearestMinutesLuxon(cphLeadTime, 30).set({ seconds: 0, milliseconds: 0 })

  const initialMaxTime = cphTime.set({ hours: 23, minutes: 30, seconds: 0, milliseconds: 0 })
  const initialMinTime = cphTime.set({ hours: 0, minutes: 30, seconds: 0, milliseconds: 0 })

  const arrivalDate = initialAbsoluteMinDateTime
  const departureDate = arrivalDate.plus({ days: 7 }).set({ hours: 17, minutes: 30, seconds: 0, milliseconds: 0 })

  useEffect(() => {
    //deliberately not using the windowWidth hook to reduce amount of re-renders.
    const handleResize = () => {
      if (window.innerWidth > 950) {
        setShowMobileBackdrop(false)
        setIsMobile(false)
      } else {
        setIsMobile(true)
      }
    }

    window.addEventListener("resize", handleResize)

    handleResize()

    parkingState.getState().setInitialValues({
      adRaw: initialAbsoluteMinDateTime.toJSDate(),
      ddRaw: departureDate.toJSDate(),
      dt: queryDt || "17:30",
      dd: queryDd ? dateHelper.formatDateWithPrevFormat(queryDd, "YYYY-MM-DD", "DD / MM / YYYY") : departureDate.toFormat("dd / MM / yyyy"),
      at: queryAt ? queryAt : arrivalDate.toFormat("HH:mm") === "00:00" ? "00:30" : arrivalDate.toFormat("HH:mm"),
      ad: queryAd ? dateHelper.formatDateWithPrevFormat(queryAd, "YYYY-MM-DD", "DD / MM / YYYY") : arrivalDate.toFormat("dd / MM / yyyy"),
      today: cphTime,
      campaignCodePromptActive: true,
      showTooltip: false,
      tooltipData: null,
      absoluteMinDateTime: initialAbsoluteMinDateTime, // absolute minimum date time for arrival date and time select
      aMinTime: initialAbsoluteMinDateTime, // arrival time select minimum time
      dMinTime: initialMinTime, // departure time select minimum time
      maxTime: initialMaxTime, // maximum time for time selects
      minDate: cphTime, // minimum date for date selects
    })

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {}, [showMobileBackdrop])

  const {
    ad,
    at,
    dd,
    dt,
    adRaw,
    ddRaw,
    affiliateCode,
    today,
    voucher,
    campaignCodePromptActive,
    changeFromBooking,
    ammendmentReference,
    ammendmentEmail,
    preventLoading,
    currentFlow,
    parkingTimeModalActive,
    absoluteMinDateTime,
    aMinTime,
    dMinTime,
    maxTime,
    minDate,
  } = parkingState((state) => ({
    ad: state.ad,
    at: state.at,
    dd: state.dd,
    dt: state.dt,
    adRaw: state.adRaw,
    ddRaw: state.ddRaw,
    affiliateCode: state.affiliateCode,
    today: state.today,
    voucher: state.voucher,
    campaignCodePromptActive: state.campaignCodePromptActive,
    showTooltip: state.showTooltip,
    tooltipData: state.tooltipData,
    changeFromBooking: state.changeFromBooking,
    ammendmentReference: state.ammendmentReference,
    ammendmentEmail: state.ammendmentEmail,
    preventLoading: state.preventLoading,
    currentFlow: state.currentFlow,
    parkingTimeModalActive: state.parkingTimeModalActive,
    absoluteMinDateTime: state.absoluteMinDateTime,
    aMinTime: state.aMinTime,
    dMinTime: state.dMinTime,
    maxTime: state.maxTime,
    minDate: state.minDate,
  }))

  const campaignCode = currentFlow === FlowEnum.STANDARDFLOW

  const getHighlightDates = () => {
    const startDate = parseDateWithFormats(ad, supportedDateFormats, { locale: dtLocale })
    const endDate = parseDateWithFormats(dd, supportedDateFormats, { locale: dtLocale })
    if (!startDate || !endDate) return []
    const duration = endDate.diff(startDate, ["days"]).toObject()
    const dayDifference = Math.floor(duration.days)

    const highlightDates = [{ "react-datepicker__day--highlighted-start": [startDate.toJSDate()] }]
    const middleDates = []
    for (let i = 1; i < dayDifference + 1; i++) {
      middleDates.push(startDate.plus({ days: i }).toJSDate())
    }
    highlightDates.push({ "react-datepicker__day--highlighted": middleDates })
    highlightDates.push({ "react-datepicker__day--highlighted-end": [endDate.toJSDate()] })
    return highlightDates
  }

  const handleCampaignCodeFocus = () => {
    if (voucher) {
      setFocusedCampaignCodeField(true)
      return
    }
    if (!voucher && focusedCampaignCodeField) {
      setFocusedCampaignCodeField(false)
      return
    }
    setFocusedCampaignCodeField(false)
  }

  useEffect(() => {
    const searchVariableCheck = ad && at && dd && dt && ad !== "" && at !== "" && dd !== "" && dt !== ""
    const qad = getQueryStringValue("ad")
    const qat = getQueryStringValue("at")
    const qdd = getQueryStringValue("dd")
    const qdt = getQueryStringValue("dt")
    const SearchChangedFromQueryString = qad != ad || qat != at || qdd != dd || qdt != dt

    if (parkingState.getState().currentFlow === FlowEnum.STANDARDFLOW && searchVariableCheck && flow) {
      console.debug("searching for standard bookings")
      performParkingSearch(
        dateHelper.formatDateWithPrevFormat(ad, "DD / MM / YYYY", "YYYY-MM-DD"),
        at,
        dateHelper.formatDateWithPrevFormat(dd, "DD / MM / YYYY", "YYYY-MM-DD"),
        dt,
        getQueryStringValue("voucher") || voucher || "",
        getQueryStringValue("affiliateCode") || affiliateCode || "",
        true,
        "",
        undefined,
        history,
      )
    }
    if (parkingState.getState().currentFlow === FlowEnum.AMENDMENTFLOWEDITTIME && searchVariableCheck && changeFromBooking) {
      console.debug("searching for new amendment time")
      performChangeBookingTime(
        changeFromBooking,
        dateHelper.formatDateWithPrevFormat(ad, "DD / MM / YYYY", "YYYY-MM-DD"),
        at,
        dateHelper.formatDateWithPrevFormat(dd, "DD / MM / YYYY", "YYYY-MM-DD"),
        dt,
        getQueryStringValue("voucher") || voucher || "",
        getQueryStringValue("affiliateCode") || affiliateCode || "",
        ammendmentReference,
        ammendmentEmail,
        true,
        "",
        history,
      )
    }
    if (parkingState.getState().currentFlow === FlowEnum.AMENDMENTFLOWCHANGECARPARK && searchVariableCheck) {
      console.debug("searching for new amendment carpark")
      performChangeBookingSearch(
        changeFromBooking,
        dateHelper.formatDateWithPrevFormat(ad, "DD / MM / YYYY", "YYYY-MM-DD"),
        at,
        dateHelper.formatDateWithPrevFormat(dd, "DD / MM / YYYY", "YYYY-MM-DD"),
        dt,
        getQueryStringValue("voucher") || voucher || "",
        getQueryStringValue("affiliateCode") || affiliateCode || "",
        ammendmentReference,
        ammendmentEmail,
        true,
        "",
        history,
      )
    }
    if (parkingState.getState().currentFlow === FlowEnum.EXTENDEDITTIME && searchVariableCheck && changeFromBooking) {
      console.debug("searching for booking extensions")
      performChangeBookingExitTime(
        changeFromBooking.booking.guid,
        dateHelper.formatDateWithPrevFormat(dd, "DD / MM / YYYY", "YYYY-MM-DD"),
        dt,
        ammendmentReference,
        ammendmentEmail,
        true,
        history,
      )
    }
  }, [ad, at, dd, dt, campaignCodePromptActive, changeFromBooking])

  useEffect(() => {
    handleCampaignCodeFocus()
  }, [voucher])

  useEffect(() => {
    if (ad && at) {
      const parsed = parseDateWithFormats(`${ad} ${at}`, supportedDateFormats, { locale: dtLocale })
      if (parsed) {
        updateFromDateTime(parsed)
      }
    }
  }, [at])

  // Unified logic for updating arrival date/time
  const updateFromDateTime = (newFrom) => {
    const { ad, at, dd, dt, absoluteMinDateTime, today } = parkingState.getState()

    // if newFrom is today and before absoluteMinDateTime, set to absoluteMinDateTime
    if (newFrom.hasSame(today, "day") && newFrom.toMillis() < absoluteMinDateTime.toMillis()) {
      newFrom = absoluteMinDateTime
    }

    // Parse previous arrival and departure
    const oldFrom = parseDateWithFormats(`${ad} ${at}`, supportedDateFormats, { locale: dtLocale })
    const oldTo = parseDateWithFormats(`${dd} ${dt}`, supportedDateFormats, { locale: dtLocale })

    // Compute previous duration object (days, hours, minutes)
    const prevDiff = oldTo.diff(oldFrom, ["days", "hours", "minutes"]).toObject()

    // Determine candidate newTo: maintain previous duration if newFrom after oldTo
    let newTo = newFrom >= oldTo ? newFrom.plus(prevDiff) : oldTo

    // Determine if newFrom is today's initial slot
    const isTodaySlot = newFrom.hasSame(today, "day") && newFrom.toMillis() === absoluteMinDateTime.toMillis()
    // Define minimum duration in hours
    const minHours = isTodaySlot ? 2 : 1
    const minDurationObj = { hours: minHours }
    const minMillis = minHours * 3600000

    // Enforce minimum duration: calculate actual diff
    const actualDiff = newTo.diff(newFrom, ["days", "hours", "minutes", "seconds"]).toObject()
    const actualMillis =
      (actualDiff.days || 0) * 86400000 + (actualDiff.hours || 0) * 3600000 + (actualDiff.minutes || 0) * 60000 + (actualDiff.seconds || 0) * 1000
    if (actualMillis < minMillis) {
      newTo = newFrom.plus(minDurationObj)
    }

    // Compute arrival minimum time based on date only
    let aMinTime
    if (newFrom.hasSame(today, "day")) {
      aMinTime = absoluteMinDateTime
    } else {
      aMinTime = newFrom.set({ hour: 0, minute: 30, second: 0, millisecond: 0 })
    }

    let dMinTime
    // If arrival and departure same day: enforce at least min duration
    if (newTo.hasSame(newFrom, "day")) {
      dMinTime = newFrom.plus(minDurationObj)
    } else {
      // different days: allow any 30-min interval
      dMinTime = newTo.set({ hour: 0, minute: 30, second: 0, millisecond: 0 })
    }

    parkingState.setState({
      adRaw: newFrom.toJSDate(),
      ddRaw: newTo.toJSDate(),
      ad: newFrom.toFormat("dd / MM / yyyy"),
      at: newFrom.toFormat("HH:mm"),
      dd: newTo.toFormat("dd / MM / yyyy"),
      dt: newTo.toFormat("HH:mm"),
      minDate: newFrom,
      aMinTime,
      dMinTime,
    })
  }

  // Handler for arrival date change
  const handleFromDateChange = (dateInput) => {
    const { ad, at } = parkingState.getState()
    const parsedTime = parseDateWithFormats(`${ad} ${at}`, supportedDateFormats, { locale: dtLocale })
    let newFrom = DateTime.fromJSDate(dateInput, { locale: dtLocale, CPH }).set({ hour: parsedTime.hour, minute: parsedTime.minute })
    updateFromDateTime(newFrom)
  }

  // Handler for arrival time change
  const handleFromTimeChange = (timeInput) => {
    const { ad, at } = parkingState.getState()
    const parsedDate = parseDateWithFormats(`${ad} ${at}`, supportedDateFormats, { locale: dtLocale })
    let newFrom = DateTime.fromJSDate(timeInput, { locale: dtLocale, CPH }).set({
      year: parsedDate.year,
      month: parsedDate.month,
      day: parsedDate.day,
    })
    updateFromDateTime(newFrom)
  }

  // Unified logic for updating departure date/time
  const updateToDateTime = (newTo) => {
    const { ad, at, absoluteMinDateTime, today } = parkingState.getState()
    const arrival = parseDateWithFormats(`${ad} ${at}`, supportedDateFormats, { locale: dtLocale })

    // Determine if arrival is earliest slot today
    const isTodaySlot = arrival.hasSame(today, "day") && arrival.toMillis() === absoluteMinDateTime.toMillis()
    const minHours = isTodaySlot ? 2 : 1
    const minDurationObj = { hours: minHours }
    const minMillis = minHours * 3600000

    // Enforce departure not before arrival + minDuration
    const actualDiff = newTo.diff(arrival, ["hours", "minutes", "seconds"]).toObject()
    const actualMillis = (actualDiff.hours || 0) * 3600000 + (actualDiff.minutes || 0) * 60000 + (actualDiff.seconds || 0) * 1000
    let adjusted = actualMillis < minMillis ? arrival.plus(minDurationObj) : newTo

    // Compute departure minimum time based on date
    let dMinTime
    if (adjusted.hasSame(arrival, "day")) {
      // same day: enforce at least min duration
      dMinTime = arrival.plus(minDurationObj)
    } else {
      // different days: 00:30 of departure date
      dMinTime = adjusted.set({ hour: 0, minute: 30, second: 0, millisecond: 0 })
    }

    // Update state
    parkingState.setState({
      ddRaw: adjusted.toJSDate(),
      dd: adjusted.toFormat("dd / MM / yyyy"),
      dt: adjusted.toFormat("HH:mm"),
      dMinTime,
    })
  }

  // Handler for departure date change
  const handleToDateChange = (dateInput) => {
    const { dd, dt } = parkingState.getState()
    // Preserve existing departure time when changing date
    const parsedTime = parseDateWithFormats(`${dd} ${dt}`, supportedDateFormats, { locale: dtLocale })
    const luxonDateInput = DateTime.fromJSDate(dateInput, { locale: dtLocale })

    const newTo =
      currentFlow === FlowEnum.EXTENDEDITTIME && extendMinDate > luxonDateInput
        ? DateTime.fromJSDate(dateInput, { locale: dtLocale }).set({ hour: extendMinDate.hour, minute: extendMinDate.minute })
        : DateTime.fromJSDate(dateInput, { locale: dtLocale }).set({
            hour: parsedTime.hour,
            minute: parsedTime.minute,
          })

    updateToDateTime(newTo)
  }

  // Handler for departure time change
  const handleToTimeChange = (timeInput) => {
    const { dd, dt } = parkingState.getState()
    const parsedDate = parseDateWithFormats(`${dd} ${dt}`, supportedDateFormats, { locale: dtLocale })
    let newTo = DateTime.fromJSDate(timeInput, { locale: dtLocale, CPH }).set({
      year: parsedDate.year,
      month: parsedDate.month,
      day: parsedDate.day,
    })
    updateToDateTime(newTo)
  }

  const searchParking = () => {
    const formattedDates = {
      ad: ad ? dateHelper.formatDateWithPrevFormat(ad, "DD / MM / YYYY", "YYYY-MM-DD") : "",
      dd: dd ? dateHelper.formatDateWithPrevFormat(dd, "DD / MM / YYYY", "YYYY-MM-DD") : "",
    }

    if (isInModal) {
      parkingState.setState({ parkingTimeModalActive: !parkingTimeModalActive })
    }
    if (parkingState.getState().currentFlow === FlowEnum.STANDARDFLOW || currentFlow === "") {
      redirectToParkingSearch(formattedDates.ad, at, formattedDates.dd, dt, searchData?.affiliate || affiliateCode)
    }
    if (parkingState.getState().currentFlow === FlowEnum.AMENDMENTCHECKOUT && changeFromBooking) {
      performChangeBookingSearch(
        changeFromBooking,
        formattedDates.ad,
        at,
        formattedDates.dd,
        dt,
        voucher,
        searchData?.affiliate || affiliateCode,
        ammendmentReference,
        ammendmentEmail,
        true,
        "",
        history,
      )
    }
  }

  const redirectToParkingSearch = (arrivalDate, arrivalTime, departureDate, departureTime, affiliateCode) => {
    const url = `${path.SEARCH(locale)}?ad=${arrivalDate}&at=${arrivalTime}&dd=${departureDate}&dt=${departureTime}${
      voucher ? "&voucher=" + voucher : ""
    }${affiliateCode ? "&affiliate=" + affiliateCode : ""}`
    window.location.assign(url)
  }

  const handleSearchButtonClick = (event) => {
    flow && containerRef.current.querySelector(".js--toggle-parking-form").click()
    searchParking()
  }

  if (!aMinTime || !dMinTime || !maxTime || !minDate || !ad || !at || !dd || !dt) {
    return null
  }

  const CustomInput = forwardRef(({ value, onClick, id, tabIndex, className, ariaLabel }, ref) => (
    <input
      id={id}
      className={className}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onClick(e)
        }
      }}
      ref={ref}
      readOnly
      role="button"
      type="text"
      value={value}
      aria-label={ariaLabel}
      tabIndex={tabIndex}
    />
  ))
  CustomInput.displayName = "CustomInput"

  const selectedAdt = parseDateWithFormats(`${ad} ${at}`, supportedDateFormats, { locale: dtLocale })?.toJSDate()
  const selectedDdt = parseDateWithFormats(`${dd} ${dt}`, supportedDateFormats, { locale: dtLocale })?.toJSDate()

  let extendMinDate
  if (changeFromBooking) {
    extendMinDate = parseDateWithFormats(
      `${changeFromBooking.booking.departureDate} ${changeFromBooking.booking.departureTime}`,
      supportedDateFormats,
    )
    DateTime.fromJSDate(extendMinDate)
  }

  const nextMonthLabel = textKeys.nextMonthAriaLabel ? textKeys.nextMonthAriaLabel : locale === "da" ? "Gå en måned frem" : "Next Month"
  const previousMonthLabel = textKeys.previousMonthAriaLabel
    ? textKeys.previousMonthAriaLabel
    : locale === "da"
      ? "Gå en måned tilbage"
      : "Next Month"
  const nextYearLabel = textKeys.nextYearAriaLabel ? textKeys.nextYearAriaLabel : locale === "da" ? "Gå et år frem" : "Next Month"
  const previousYearLabel = textKeys.previousYearAriaLabel ? textKeys.previousYearAriaLabel : locale === "da" ? "Gå et år tilbage" : "Previous Year"

  const showPickersCheck =
    currentFlow !== FlowEnum.AMENDMENTFLOWEDITTIME && currentFlow !== FlowEnum.EXTENDEDITTIME && flow && isMobile && !showMobileBackdrop
      ? " v-hidden"
      : ""

  let tabindexCheck = 0
  if (
    isMobile &&
    !showMobileBackdrop &&
    currentFlow !== FlowEnum.STANDARDFLOW &&
    currentFlow !== FlowEnum.AMENDMENTFLOWEDITTIME &&
    currentFlow !== FlowEnum.EXTENDEDITTIME
  ) {
    tabindexCheck = -1
  }

  //outside booking context
  if (currentFlow == "") {
    tabindexCheck = 0
  }

  return (
    <Fragment>
      <div className={`parking-time-picker__form form  js--parking-form${showMobileBackdrop ? " show" : ""}${showPickersCheck}`} ref={containerRef}>
        {flow && (!currentFlow === FlowEnum.AMENDMENTFLOWEDITTIME || !currentFlow === FlowEnum.EXTENDEDITTIME) ? (
          <div className="parking-time-picker__mobile-only">
            <p>{textKeys?.newSearch || "Ny søgning"}</p>
            <button
              ref={mobileCloseBtnRef}
              onClick={() => setShowMobileBackdrop(!showMobileBackdrop)}
              className="js--toggle-parking-form"
              aria-label={locale == "da" ? "Luk booking søgning" : "close booking search"}
              tabIndex={tabindexCheck}
            >
              <svg className="svg-icon">
                <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="/dist/svg/symbols.svg#svg-close">
                  &nbsp;
                </use>
              </svg>
            </button>
          </div>
        ) : (
          ""
        )}
        {!ad || ad !== "" ? (
          <Fragment>
            {currentFlow !== FlowEnum.EXTENDEDITTIME ? (
              <div
                ref={fromDateSelectRef}
                className={`form__section dateselect${flow || isInModal ? " in-flow" : ""}${showMobileBackdrop ? " backdrop" : ""}`}
              >
                <label className="from-date-label" htmlFor="from-date">
                  {textKeys?.from || "From"}
                </label>
                <DatePicker
                  id="from-date"
                  ref={fromDatePickerRef}
                  tabIndex={tabindexCheck}
                  customInput={
                    <CustomInput
                      className=""
                      ariaLabel={`${locale == "da" ? "Ankomsts datovælger. Åben dato-vælgeren med 'Enter'. Tab derefter videre for at komme ind i ankomsts datovælgeren, brug piletaster og 'Enter' til at vælge en ankomstsdato." : "arrival date picker. Press 'Enter' to open the date picker, then use Tab to enter the arrival date picker, use arrow keys and 'Enter' to select an arrival date"}`}
                    />
                  }
                  isClearable={false}
                  selected={selectedAdt}
                  onChange={(date) => handleFromDateChange(date)}
                  dateFormat={locale === "en" ? "yyyy-MM-dd" : "dd / MM / yyyy"}
                  minDate={cphTime.toJSDate()}
                  locale={locale === "en" ? enGB : da}
                  highlightDates={getHighlightDates()}
                  showTimeSelect={false}
                  chooseDayAriaLabelPrefix={locale == "da" ? "Vælg" : "Choose"}
                  nextMonthAriaLabel={nextMonthLabel}
                  nextYearAriaLabel={nextYearLabel}
                  previousMonthAriaLabel={previousMonthLabel}
                  previousYearAriaLabel={previousYearLabel}
                  useWeekdaysShort={true}
                />
                <div ref={fromTimeWrapperRef} className="from-time-wrapper">
                  <label className="from-time-label" htmlFor="from-time">
                    {textKeys?.time || "TID"}
                  </label>
                  <DatePicker
                    id="from-time"
                    ref={fromTimePickerRef}
                    tabIndex={tabindexCheck}
                    customInput={
                      <CustomInput
                        className=""
                        ariaLabel={`${locale == "da" ? "Ankomst tidsvælger. Åben tidspunktsvælgeren med 'Enter'. Tab derefter videre for at komme ind i ankomststidspunkts-vælgeren, brug piletaster og 'Enter' til at vælge et ankomststidspunkt." : "Arrival time picker. Press 'Enter' to open the time picker, then use Tab to enter the arrival time picker, use arrow keys and 'Enter' to select an arrival time"}`}
                      />
                    }
                    isClearable={false}
                    selected={selectedAdt}
                    onChange={(time) => handleFromTimeChange(time)}
                    dateFormat={locale === "en" ? "h:mm aa" : "HH:mm"}
                    minDate={cphTime.toJSDate()}
                    minTime={aMinTime.toJSDate()}
                    maxTime={maxTime.toJSDate()}
                    timeInputLabel={textKeys?.time || "TID"}
                    locale={locale === "en" ? enGB : da}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    filterTime={filterDstHour}
                  />
                </div>
              </div>
            ) : undefined}
            <div
              ref={toDateSelectRef}
              className={`form__section dateselect${flow || isInModal ? " in-flow" : ""} ${showMobileBackdrop ? " backdrop" : ""}`}
            >
              <label className="to-date-label" htmlFor="to-date">
                {textKeys?.to || "TIL"}
              </label>
              <DatePicker
                id="to-date"
                ref={toDatePickerRef}
                tabIndex={tabindexCheck}
                customInput={
                  <CustomInput
                    className=""
                    ariaLabel={`${locale == "da" ? "Udkørsels datovælger. Åben dato-vælgeren med 'Enter'. Tab derefter videre for at komme ind i udkørsels datovælgeren, brug piletaster og 'Enter' til at vælge en udkørselsdato." : "Departure date picker. Press 'Enter' to open the date picker, then use Tab to enter the departure date picker, use arrow keys and 'Enter' to select a departure date"}`}
                  />
                }
                isClearable={false}
                selected={selectedDdt}
                onChange={(date) => handleToDateChange(date)}
                dateFormat={locale === "en" ? "yyyy-MM-dd" : "dd / MM / yyyy"}
                minDate={currentFlow === FlowEnum.EXTENDEDITTIME && changeFromBooking ? extendMinDate.toJSDate() : minDate.toJSDate()}
                locale={locale === "en" ? enGB : da}
                highlightDates={getHighlightDates()}
                showTimeSelect={false}
                chooseDayAriaLabelPrefix={locale == "da" ? "Vælg" : "Choose"}
                nextMonthAriaLabel={nextMonthLabel}
                nextYearAriaLabel={nextYearLabel}
                previousMonthAriaLabel={previousMonthLabel}
                previousYearAriaLabel={previousYearLabel}
                useWeekdaysShort={true}
              />
              <div ref={toTimeWrapperRef} className="to-time-wrapper">
                <label className="to-time-label" htmlFor="to-time">
                  {textKeys?.time || "TID"}
                </label>
                <DatePicker
                  id="to-time"
                  ref={toTimePickerRef}
                  tabIndex={tabindexCheck}
                  customInput={
                    <CustomInput
                      className=""
                      ariaLabel={`${locale == "da" ? "Udkørselstidspunktsvælger. Åben tidspunktsvælgeren med 'Enter'. Tab derefter videre for at komme ind i udkørselstidspunkts-vælgeren, brug piletaster og 'Enter' til at vælge et udkørselstidspunkt." : "Departure time picker. Press 'Enter' to open the time picker, then use Tab to enter the departure time picker, use arrow keys and 'Enter' to select a departure time"}`}
                    />
                  }
                  isClearable={false}
                  selected={selectedDdt}
                  onChange={(time) => handleToTimeChange(time)}
                  dateFormat={locale === "en" ? "h:mm aa" : "HH:mm"}
                  minDate={currentFlow === FlowEnum.EXTENDEDITTIME && changeFromBooking ? extendMinDate.toJSDate() : minDate.toJSDate()}
                  minTime={
                    currentFlow === FlowEnum.EXTENDEDITTIME && changeFromBooking && extendMinDate && extendMinDate.hasSame(selectedDdt, "day")
                      ? extendMinDate.toJSDate()
                      : dMinTime.toJSDate()
                  }
                  maxTime={maxTime.toJSDate()}
                  timeInputLabel={textKeys?.time || "TID"}
                  locale={locale === "en" ? enGB : da}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={30}
                  filterTime={filterDstHour}
                />
              </div>
            </div>
          </Fragment>
        ) : (
          ""
        )}
        {flow && campaignCode ? (
          <React.Fragment>
            <div
              className={`form__section form__section--light parking-time-picker__campaigncode js--campaign-code ${
                campaignCode && campaignCodePromptActive ? "show" : ""
              }`}
            >
              <div id="campaigncodecontainer" className="form__section__inner">
                <div className={`form__field form__field--text${focusedCampaignCodeField ? " is-filled" : ""}`}>
                  <span className="form__valid"></span>
                  <input
                    className="form__item "
                    tabIndex={flow && isMobile && !showMobileBackdrop ? "-1" : "0"}
                    type="text"
                    id="campaigncode"
                    value={voucher || ""}
                    onFocus={() => handleCampaignCodeFocus()}
                    onBlur={() => handleCampaignCodeFocus()}
                    onChange={(e) => parkingState.setState({ voucher: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === 13) {
                        parkingState.setState({ campaignCodePromptActive: false, modalOpenerId: "removecampaigncodebutton" })
                      }
                    }}
                  ></input>
                  <label htmlFor="campaigncode" className="form__label">
                    <span id="campaigncodelabelinner" className="form__label__text">
                      {textKeys?.promotionCode || "KampagnekodeX"}&nbsp;&nbsp;
                      <strong>&nbsp;</strong>
                    </span>
                  </label>
                </div>
              </div>
              <div id="campaigncodesubmit" className="form__section__inner">
                <button
                  id="campaignsubmitbutton"
                  className=" button--text-link  parking-time-picker__applycode js--open-modal"
                  data-template="parkingWrongCampaignCodeModalTemplate"
                  onClick={
                    voucher && voucher.length > 0
                      ? () => parkingState.setState({ campaignCodePromptActive: false, modalOpenerId: "removecampaigncodebutton" })
                      : undefined
                  }
                  tabIndex={flow && isMobile && !showMobileBackdrop ? "-1" : "0"}
                >
                  {textKeys?.promotionCodeApply || "Brug"}
                </button>
              </div>
            </div>
            <div
              className={`form__section form__section--light parking-time-picker__campaigncode-applied js--campaign-code-applied ${
                !campaignCodePromptActive ? "show" : ""
              }`}
            >
              <div className="form__section__inner">
                <span className="t--bold">{voucher}</span>
              </div>
              <div className="form__section__inner">
                <button
                  id="removecampaigncodebutton"
                  className=" button--text-link js--applycode parking-time-picker__applycode"
                  onClick={() => {
                    parkingState.setState({ campaignCodePromptActive: true, voucher: "" })
                    const currentVoucher = getQueryStringValue("voucher")
                    if (currentVoucher) {
                      setQueryStringValue("voucher", undefined)
                    }
                  }}
                >
                  {textKeys?.promotionCodeRemove || "Fjern"}
                </button>
              </div>
            </div>
          </React.Fragment>
        ) : (
          ""
        )}
        {currentFlow === FlowEnum.STANDARDFLOW || currentFlow === "" ? (
          <div className="form__section parking-time-picker__cta">
            <div className="form__section__inner">
              <button
                className={`button js-book button--large ${buttonModifiers || (!slimVersion ? "button--general button--large" : "button--medium button--general-inverted")} ${buttonFontColor}`}
                onClick={handleSearchButtonClick}
                tabIndex={flow && isMobile && !showMobileBackdrop ? "-1" : "0"}
              >
                <span className="text">{button || textKeys?.button || "Book nu"}</span>
              </button>
            </div>
          </div>
        ) : undefined}
        {showMobileBackdrop ? (
          <button
            aria-label={locale === "en" ? "Closing parking details menu." : "Lukker parkerings menu."}
            onFocus={() => setShowMobileBackdrop(false)}
            className="js--toggle-parking-form parking-time-picker__backdrop"
          />
        ) : (
          ""
        )}
      </div>
      {flow && !showMobileBackdrop ? (
        <div className="parking-time-picker__preview js--parking-preview">
          <div>
            <p className="text">{ad ? dateHelper.formatDateWithPrevFormat(ad, "DD / MM / YYYY", "DD MMM. YYYY") : ""}</p>
            <p className="text-small">{textKeys?.timeFormat?.formatUnicorn({ time: at }) || `kl. ${at || ""}`}</p>
          </div>
          <div>
            <svg className="svg-icon">
              <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="/dist/svg/symbols.svg#svg-arrowright-white">
                &nbsp;
              </use>
            </svg>
          </div>
          <div>
            <p className="text">{dd ? dateHelper.formatDateWithPrevFormat(dd, "DD / MM / YYYY", "DD MMM. YYYY") : ""}</p>
            <p className="text-small">{textKeys?.timeFormat?.formatUnicorn({ time: dt }) || `kl. ${dt || ""}`}</p>
          </div>
          <button
            aria-label={locale === "da" ? "Rediger booking detaljer eller tilføj kampagnekode" : "Edit booking details or add a campaign code"}
            onClick={() => handleOpenMobileBackdrop()}
            className="parking-time-picker__preview-toggle js--toggle-parking-form"
          >
            {currentFlow === FlowEnum.STANDARDFLOW ? (
              <svg aria-hidden="true" tabIndex={-1} className="svg-icon">
                <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="/dist/svg/symbols.svg#svg-edit-simple">
                  &nbsp;
                </use>
              </svg>
            ) : null}
          </button>
        </div>
      ) : (
        ""
      )}
      {campaignCode && !showMobileBackdrop ? (
        //there already is a button right above this one wich serves the same purpose.
        //it's redundancy for screenreader users, hence why it's tabindex="-1"
        <button tabIndex="-1" onClick={() => handleOpenMobileBackdrop()} className="js--toggle-parking-form mobile-campaign-btn">
          {textKeys?.promotionCodePrompt || "Kampagnekode?"}
        </button>
      ) : (
        ""
      )}
    </Fragment>
  )
}
